import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getPasskey } from '../utils/passkeys';
import AuthService from '../services/AuthService';
import UserService from '../services/UserService';
import { TOKEN_KEY, USER_KEY } from '../services/api';
import i18n, { normalizeLang } from '../i18n';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const readUser = () => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
};

/** Switches the UI to the language saved on the account (if any). */
const applyUserLanguage = (user) => {
    const lng = normalizeLang(user?.language);
    if (lng && lng !== i18n.language) i18n.changeLanguage(lng);
};

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [user, setUser] = useState(readUser);

    const storeUser = useCallback((u) => {
        localStorage.setItem(USER_KEY, JSON.stringify(u));
        setUser(u);
    }, []);

    const storeSession = useCallback((data) => {
        localStorage.setItem(TOKEN_KEY, data.token);
        setToken(data.token);
        storeUser(data.user);
        applyUserLanguage(data.user);
    }, [storeUser]);

    // Refresh the profile once per app load, so a language changed on another device is picked up.
    useEffect(() => {
        if (!token) return;
        AuthService.me().then((u) => { storeUser(u); applyUserLanguage(u); }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const login = useCallback(async (email, password) => {
        const data = await AuthService.login(email, password);
        // With 2FA on, the server withholds the token and returns a challenge for the second step.
        if (data.twoFactorRequired) return data;
        storeSession(data);
        return data;
    }, [storeSession]);

    const verifyTwoFactor = useCallback(async (challenge, code) => {
        const data = await AuthService.twoFactorVerify(challenge, code);
        storeSession(data);
        return data.user;
    }, [storeSession]);

    /** Face ID / fingerprint sign-in: the device signs the server's challenge; no email or password. */
    const loginWithPasskey = useCallback(async () => {
        const { requestId, publicKey } = await AuthService.passkeyLoginStart();
        const credential = await getPasskey(publicKey);
        const data = await AuthService.passkeyLoginFinish(requestId, credential);
        storeSession(data);
        return data.user;
    }, [storeSession]);

    const register = useCallback(async (payload) => {
        // The firm's starter categories are created in the language used for registration.
        const data = await AuthService.register({ ...payload, language: i18n.language });
        storeSession(data);
        return data.user;
    }, [storeSession]);

    /** Invited employee sets their password; the server signs them in straight away. */
    const acceptInvite = useCallback(async (payload) => {
        const data = await UserService.acceptInvite(payload);
        if (data.twoFactorRequired) return data;
        storeSession(data);
        return data;
    }, [storeSession]);

    const updateUser = useCallback((patch) => {
        setUser(prev => {
            const next = { ...prev, ...patch };
            localStorage.setItem(USER_KEY, JSON.stringify(next));
            return next;
        });
    }, []);

    /** Changes the UI language immediately; when signed in, also saves it on the account. */
    const setLanguage = useCallback((code) => {
        i18n.changeLanguage(code);
        if (token) {
            AuthService.setLanguage(code).then(storeUser).catch(() => { /* UI already switched; retry on next change */ });
        }
    }, [token, storeUser]);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setToken(null);
        setUser(null);
    }, []);

    // Employees see no prices, payments, quotes or firm settings (the server enforces the same).
    const isAdmin = user?.role === 'ADMIN';

    const value = useMemo(() => ({
        isLoggedIn: !!token, user, isAdmin, login, verifyTwoFactor, loginWithPasskey, register, acceptInvite, logout, updateUser, setLanguage,
    }), [token, user, isAdmin, login, verifyTwoFactor, loginWithPasskey, register, acceptInvite, logout, updateUser, setLanguage]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
